import { KakeruInitParam, PatientBackgroundPayload } from '../types/embed'

// 親から受けた頭書きを、back へ送る形に絞る。
//
// ここが「どの区分を SOAP 生成の根拠に使うか」の唯一の定義。親(AnyCOMPASS)側は
// 構造を圧縮して渡してくるだけで、区分の取捨は一切していない。方針をこちら側に置くのは、
// 対象を足す/減らすが当社リポジトリのデプロイだけで済むようにするため
// (親は先方リポジトリなので、変更のたびに先方の配備を待つことになる)。
//
// 方針は「標準区分は全部取る」。頭書きは薬歴の一部で、どの区分も服薬指導の根拠になりうる
// (指導相手・嗜好品・職業・後発品の意向 も含む)。区分ごとに SOAP でどう使うかを決めるのは
// back の prompt (patient_background yaml) の責務で、ここでは区分を取捨しない。
// 店舗が自由に足せる区分 (91〜99 等) だけは入れない: 名称も意味も店舗ごとに違い、規範が書けない。
//
// 副作用歴・アレルギー歴・病態・検査値は要配慮個人情報。中身の無い区分は送らず、
// チェック済み特記と区分コメントだけを残す (マスタ全量を当社 DB に置かない)。

// 親の標準区分は 01〜14 / 16〜25 (15 は欠番、99 は定義だけで未使用)。
// 番号と名称の対応表は先方コードにも無い (24=注意薬 / 25=副作用歴(薬品) のコメントだけ) ので、
// 個別に列挙せずレンジで判定する。中身の無い区分は後段のフィルタが落とすので、
// レンジを広げても payload は膨らまない。
const isStandardKbn = (kbn: string): boolean =>
  /^\d{2}$/.test(kbn) && kbn >= '01' && kbn <= '25'

const text = (value: string | undefined): string => value?.trim() ?? ''

// 検査値は区分 23 にぶら下がって届くので、body では独立させる
// (back の `# 患者背景` で O 転記の指示が区分の羅列とは別行になるため)。
//
// 引数は INIT の param 丸ごと。頭書き(omotegaki)と身長体重(knjWeight/knjHeight)は
// 親の中では別画面(MCYW0090 / MCYW0010)由来だが、こちら側では同じ患者背景に合流する。
// 位置引数に並べない: 体重と身長は同じ型の隣り合う省略可能引数なので取り違えても型で落ちない。
export const buildPatientBackgroundPayload = (
  param: KakeruInitParam | null | undefined
): PatientBackgroundPayload => {
  const source = param?.omotegaki ?? []

  const items = source
    .filter((item) => isStandardKbn(text(item.kbn)))
    .map((item) => ({
      kbn: text(item.kbn),
      name: text(item.name),
      note: text(item.note),
      tokki: (item.tokki ?? []).map(text).filter(Boolean),
      checked_by: text(item.checkedBy),
      checked_at: text(item.checkedAt),
    }))
    // チェック済み特記も区分コメントも無い区分は、登録が無いのと同じなので送らない。
    .filter((item) => item.tokki.length > 0 || item.note !== '')

  // 検査値には、載っていた区分の最終確認日を添える (区分23 の検査値は入力するとすぐ届く値で、
  // その日付が「いつの値か」の唯一の手がかり)。back は日付の無い値を「登録日不明」と書く。
  // 日付が無ければキーごと送らない (back の受け口は任意。旧 body と同じ形)。
  const vitals = source
    .filter((item) => isStandardKbn(text(item.kbn)))
    .flatMap((item) =>
      (item.vitals ?? []).map((vital) => {
        const checkedAt = text(item.checkedAt)
        return {
          name: text(vital.name),
          value: text(vital.value),
          unit: text(vital.unit),
          ...(checkedAt !== '' ? { checked_at: checkedAt } : {}),
        }
      })
    )
    .filter((vital) => vital.value !== '')

  // 身長・体重には back の受領形に専用の口が無いので、検査値と同じ器に載せる。
  // 患者情報由来は画面を開いた時点の値で日付が無い (区分23 にあれば同じ値が上の vitals にも
  // 日付つきで載る。名寄せは back。hub log/78 wl/031・032)。
  // 単位は空にする: 届く値が単位込みの文字列なので、足すと '10.0kgkg' になる。
  // 体重を先頭に置くのは、用量の妥当性評価(特に乳幼児)で最初に参照される値だから。
  const bodyVitals = [
    { name: '体重', value: text(param?.knjWeight), unit: '' },
    { name: '身長', value: text(param?.knjHeight), unit: '' },
  ].filter((vital) => vital.value !== '')

  // knjName は頭書きの中身ではなく出所表示 (INIT で受けた氏名の echo)。back の突合用。
  return {
    knjName: text(param?.knjName),
    items,
    vitals: [...bodyVitals, ...vitals],
  }
}

// 送る中身が 1 件も無いなら PUT しない(空レコードを作らない)。
// 身長体重だけでも中身ありとして扱う(頭書きが白紙の患者でも体重は SOAP の根拠になる)。
// back は「レコードが無い = 頭書き未受領」「中身が空 = セクション省略」を同じに扱うが、
// 無駄な書き込みで要配慮個人情報の器だけ増やす意味は無い。
export const hasPatientBackground = (
  payload: PatientBackgroundPayload
): boolean => payload.items.length > 0 || payload.vitals.length > 0
